/* eslint-disable @typescript-eslint/no-explicit-any */

import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import * as jwt_decode from 'jwt-decode';
import { Observable, throwError, of, interval } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { RoutingService } from './routing.service';
import { ConfigService } from './config.service';
import { NgHttpCachingService } from 'ng-http-caching';
import { BannerService } from './ai-banner.service';
import {
  getScoreNameInfo,
  getScoresInfo,
  getScoreType,
} from '../shared/utils/score-type-details';
import { UserStateService } from '../shared/services/user-state.service';
import { CustomerApiService } from '../shared/services/customer-api/customer-api.service';
import { UserActivityApiService } from '../shared/services/customer-api/user-activity-api.service';
import { PortfolioApiService } from '../shared/services/customer-api/portfolio-api.service';
import { FeatureFlagsService } from '../shared/services/customer-api/feature-flags.service';
import { QueryApiService } from '../shared/services/customer-api/query-api.service';
import { TokenService } from '../shared/services/customer-api/token.service';
import { UserInfoService } from '../shared/services/user-info.service';

// Token re-validation cadence: re-validate the session JWT every 15 minutes.
const TOKEN_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

/**
 * This service manage all the user activity like login, report, annotation, portfolio, query etc...
 */
@Injectable()
export class AuthService {
  get token(): string {
    return this.tokenService.token;
  }
  set token(value: string) {
    this.tokenService.token = value;
  }

  get tokenDataAccess(): string {
    return this.tokenService.tokenDataAccess;
  }
  set tokenDataAccess(value: string) {
    this.tokenService.tokenDataAccess = value;
  }

  get jwtAuthorized(): boolean {
    return this.tokenService.jwtAuthorized;
  }
  set jwtAuthorized(value: boolean) {
    this.tokenService.jwtAuthorized = value;
  }

  /**
   * this is a duplicate of the token
   */
  userdata: any;

  /**
   * subscribe every 15 minutes to check if the token is expired, if expired back to login,
   * validate it to get new token and save it in the session storage
   */
  constructor(
    private router: Router,
    public routing: RoutingService,
    public config: ConfigService,
    private ngHttpCachingService: NgHttpCachingService,
    private bannerService: BannerService,
    private userState: UserStateService,
    private customerApi: CustomerApiService,
    private userActivityApi: UserActivityApiService,
    private portfolioApi: PortfolioApiService,
    private queryApi: QueryApiService,
    private featureFlagsService: FeatureFlagsService,
    private tokenService: TokenService,
    private userInfo: UserInfoService,
  ) {
    this.tokenService.configure({
      useStorage: this.config.appConfig.setup.useStorage,
      isReaderContext: this.routing.isReader() || this.routing.isStoryReader(),
    });

    interval(TOKEN_REFRESH_INTERVAL_MS).subscribe(() => {
      if (!this.routing.isReader() && !this.routing.isStoryReader()) {
        const user = this.authToken();
        if (user) {
          const expire = new Date(user.exp * 1000);
          const now = new Date();
          if (expire < now) {
            this.router.navigate(['/login']);
          }
        } else {
          if (!this.routing.isLogin()) {
            this.router.navigate(['/login']);
          }
        }
        if (this.token) {
          this.customerApi
            .post<any>('/v5/validate/jwt', { token: this.token })
            .subscribe({
              next: (result) => {
                this.token = result.token;
                this.tokenDataAccess = result.data_access_token;
                // Routes to sessionStorage while impersonating, so this refresh
                // never rewrites the shared localStorage `token` key.
                this.tokenService.persistTokens();
              },
              error: (error: HttpErrorResponse) => {
                // Only bounce to login on an auth failure. A transient 5xx or
                // dropped connection must not log the user out — this POST is not
                // covered by the interceptor's GET-only retry.
                if (error?.status === 401 || error?.status === 403) {
                  this.router.navigate(['/login']);
                }
              },
            });
        }
      }
    });
    window.addEventListener('storage', (event) => {
      // Ignore every key but `token` BEFORE decoding — otherwise a cross-tab
      // write of theme/search/etc. runs jwt_decode('dark') and throws here.
      if (event.key !== 'token') {
        return;
      }
      // An impersonation tab's session lives in sessionStorage; changes to the
      // shared localStorage `token` concern the admin session in another tab and
      // must not log this tab out.
      if (this.tokenService.impersonating) {
        return;
      }
      const emailOf = (value: string | null): string | null =>
        value ? (jwt_decode(value) as any).email : null;
      let currentUser: string | null = null;
      let previousUser: string | null = null;
      try {
        currentUser = emailOf(event.newValue);
        previousUser = emailOf(event.oldValue);
      } catch {
        return;
      }
      if (
        !currentUser ||
        (currentUser && previousUser && currentUser !== previousUser)
      ) {
        this.router.navigate(['/login']);
      }
    });
  }

  // ERROR ---------------------------------------------------------------------

  /**
   * Console log error in case of fail request
   */
  private handleError(error: HttpErrorResponse) {
    let message = 'Something bad happened please try again later.';
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
      message = error.error.message;
    } else {
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`,
      );
      message = error.error;
    }
    return throwError(() => new Error(message));
  }

  // FEATURE FLAGS ------------------------------------------------------------

  /**
   * Loads remote user-specific configuration after login. Delegates to
   * FeatureFlagsService.load — see that service for the orchestration.
   */
  loadFeatureFlags(token: string): Observable<void> {
    return this.featureFlagsService.load(token);
  }

  // TRACKING USER INTERACTIONS WITH COCKPIT

  /**
   * track users login sessions
   */
  trackLoginSession(isImpersonated): Observable<any> {
    const params = isImpersonated ? { impersonated: 'true' } : undefined;
    return this.customerApi
      .get('/v1/cockpit_sessions/start', params)
      .pipe(catchError((error) => this.handleError(error)));
  }

  updateAccountInfo(accountInfo: any) {
    this.featureFlagsService.updateAccountInfo(accountInfo);
  }

  /**
   * Score-info delegates. Implementation lives in score-type-details.ts;
   * AuthService just forwards the user's current scorelabSettings. Callers
   * may import the utility functions directly — these wrappers exist for
   * template binding ergonomics (no constructor injection needed in HTML).
   */
  getScoreName() {
    return getScoreNameInfo(this.featureFlagsService.scorelabSettings);
  }

  getScoresInfo(id?: string) {
    return getScoresInfo(this.featureFlagsService.scorelabSettings, id);
  }

  getScoreType(id?: string) {
    return getScoreType(this.featureFlagsService.scorelabSettings, id);
  }

  // AUTH ---------------------------------------------------------------------

  /**
   * autentification of the user login
   */
  authLogin(email, password): Observable<any> {
    return this.customerApi
      .post<any>('/v5/authenticate', { email, password })
      .pipe(
        tap((result) => {
          if (!result['two_factor_auth']) {
            const data: any = result;
            this.token = data.token;
            const user = jwt_decode(data.token);
            this.tokenDataAccess = data.data_access_token;
            this.tokenService.persistTokens();
            this.loadFeatureFlags(this.token).subscribe();
            this.userActivityApi.getSeenArticles().subscribe((articles) => {
              this.userActivityApi.seenArticles = articles;
            });
            if (this.config.appConfig.setup.useStorage) {
              if (sessionStorage.getItem('track_login') !== user.email) {
                sessionStorage.setItem('track_login', user.email);
                if (localStorage.getItem('impersonate') === user.email) {
                  this.trackLoginSession(true).subscribe();
                } else {
                  this.trackLoginSession(false).subscribe();
                }
              }
            }

            // Check and show AI banner modal after successful login
            setTimeout(() => {
              this.bannerService.checkAndShowBanner();
            }, 1000); // Small delay to ensure the user has navigated
          }
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * autentification of the user login
   */
  authLogin2fa(email, code): Observable<any> {
    return this.customerApi
      .post<any>('/v5/authenticate2f', { email, code })
      .pipe(
        tap((result) => {
          const data: any = result;
          this.token = data.token;
          const user = jwt_decode(data.token);
          this.tokenDataAccess = data.data_access_token;
          this.tokenService.persistTokens();
          this.loadFeatureFlags(this.token).subscribe();
          this.userActivityApi.getSeenArticles().subscribe((articles) => {
            this.userActivityApi.seenArticles = articles;
          });
          if (this.config.appConfig.setup.useStorage) {
            if (sessionStorage.getItem('track_login') !== user.email) {
              sessionStorage.setItem('track_login', user.email);
              if (localStorage.getItem('impersonate') === user.email) {
                this.trackLoginSession(true).subscribe();
              } else {
                this.trackLoginSession(false).subscribe();
              }
            }
          }

          // Check and show AI banner modal after successful 2FA login
          setTimeout(() => {
            this.bannerService.checkAndShowBanner();
          }, 1000); // Small delay to ensure the user has navigated
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * autentification of the token
   */
  authLoged(): Observable<any> {
    this.token = this.tokenService.getActiveToken();
    if (!this.token) {
      return of({});
    }
    return this.customerApi
      .post<any>('/v5/validate/jwt', { token: this.token })
      .pipe(
        tap((result) => {
          const data: any = result;
          this.token = data.token;
          const user = jwt_decode(data.token);
          this.tokenDataAccess = data.data_access_token;
          if (!this.routing.isReader() && !this.routing.isStoryReader()) {
            this.tokenService.persistTokens();
            this.loadFeatureFlags(this.token).subscribe();
            this.userActivityApi.getSeenArticles().subscribe((articles) => {
              this.userActivityApi.seenArticles = articles;
            });

            // Re-show AI banner if 24h+ passed since it was last shown
            setTimeout(() => {
              this.bannerService.checkAndShowBannerIfDue();
            }, 1000);
          }
          this.jwtAuthorized = true;
          if (this.config.appConfig.setup.useStorage) {
            if (sessionStorage.getItem('track_login') !== user.email) {
              sessionStorage.setItem('track_login', user.email);
              if (localStorage.getItem('impersonate') === user.email) {
                this.trackLoginSession(true).subscribe();
              } else {
                this.trackLoginSession(false).subscribe();
              }
            }
          }
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * autentification of the token
   */
  jwtAuth(token): Observable<any> {
    return this.customerApi.post<any>('/v5/validate/jwt', { token }).pipe(
      tap((result) => {
        const data: any = result;
        this.token = data.token;
        const user = jwt_decode(data.token);
        this.userdata = user;
        this.tokenDataAccess = data.data_access_token;
        let readonlyToken = false;
        if (
          user &&
          ((user.roles && user.roles.includes('WIDGET_READONLY')) ||
            (user.roles && user.roles.includes('WIDGET_READONLY')))
        ) {
          readonlyToken = true;
        }
        if (
          !this.routing.isReader() &&
          !this.routing.isStoryReader() &&
          !readonlyToken
        ) {
          // Impersonation arrives here; persistTokens routes to sessionStorage
          // so it never touches the admin tab's localStorage `token`.
          this.tokenService.persistTokens();
          this.loadFeatureFlags(this.token).subscribe();
          this.userActivityApi.getSeenArticles().subscribe((articles) => {
            this.userActivityApi.seenArticles = articles;
          });
        }
        if (
          !this.routing.isReader() &&
          !this.routing.isStoryReader() &&
          readonlyToken
        ) {
          this.router.navigate(['/message/autherror']);
        } else {
          this.jwtAuthorized = true;
        }
        if (this.config.appConfig.setup.useStorage) {
          if (sessionStorage.getItem('track_login') !== user.email) {
            sessionStorage.setItem('track_login', user.email);
            if (localStorage.getItem('impersonate') === user.email) {
              this.trackLoginSession(true).subscribe();
            } else {
              this.trackLoginSession(false).subscribe();
            }
          }
        }
      }),
      catchError((error) => this.handleError(error)),
    );
  }

  // JWT payload (email, roles, exp, …). Decode happens once per token via
  // UserInfoService — repeat calls return the cached payload.
  authToken() {
    this.userdata = this.userInfo.user;
    return this.userdata;
  }

  /**
   * logout
   */
  authLogout() {
    this.portfolioApi.invalidateCache();
    this.queryApi.invalidateCache();
    this.queryApi.resetState();
    this.userInfo.clear();
    this.bannerService.stopDailyWatcher();

    // Clears token + tokenDataAccess + impersonate, in memory and in storage.
    // Without this the data-access token survives on disk and the in-memory
    // token keeps being served by getActiveToken() after an SPA logout.
    // User-scoped view state is namespaced per account (UserScopedStorageService),
    // so it is isolated between users without wiping — nothing to clear here.
    this.tokenService.clear();
    if (this.config.appConfig.setup.useStorage) {
      sessionStorage.removeItem('track_login');
    }
    this.ngHttpCachingService.clearCache();
    this.featureFlagsService.invalidate();
    this.userState.defaultFilter = undefined;
    this.userState.filter = undefined;
    this.router.navigate(['/login']);
  }

  /**
   * change password knowing the old password
   */
  setPassword(password1, password2): Observable<any> {
    this.token = this.tokenService.getActiveToken();
    return this.customerApi
      .postText('/v5/change_password', {
        old_password: password1,
        new_password: password2,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * request password reset for a specific email
   */
  requestPasswordReset(email, url): Observable<any> {
    return this.customerApi
      .postText('/v5/request_password_reset', {
        email,
        password_reset_url: url,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * reset password based on the received token
   */
  resetPassword(email, token, password): Observable<any> {
    return this.customerApi
      .postText('/v5/reset_password', {
        email,
        token,
        new_password: password,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  // STATUS ---------------------------------------------------------------
}
